import React, { useMemo, useRef, useState } from 'react';
import BaseTable from 'react-base-table';
import AutoSizer from 'react-virtualized-auto-sizer';
import classNames from 'classnames';
import { sortableContainer, sortableHandle } from 'react-sortable-hoc';
import { __, getLocalized } from '../../../core/utils';
import Tooltip from '../../../components/Tooltip';
import Dropdown from '../../../components/Dropdown';
import { hideDropdown } from '../../../redux/actions/general/dropdown';
import GrabberIcon from '../../../assets/images/grabber-16x16.svg';
import CheckedIcon from '../../../assets/images/checked-15x15.svg';
import UncheckedIcon from '../../../assets/images/unchecked-15x15.svg';
import MenuHIcon from '../../../assets/images/menu-h-15x15.svg';
import EditIcon from '../../../assets/images/edit-16x16.svg';
import DeleteIcon from '../../../assets/images/delete-15x15.svg';
import LogoIcon from '../../../assets/images/logo-50x50.svg';
import PlanningLibraryRow from './PlanningLibraryRow';
import styles from '../../../styles/views/company/PlanningTemplates.scss';

const DraggableContainer = sortableContainer(({ children }) => children);
const DragHandle = sortableHandle(() => (
  <div className={styles.grabber}>
    <GrabberIcon />
  </div>
));

const PlanningLibraryTable = ({
  canDeleteSystem = false,
  canEditSystem = false,
  data,
  emptyText,
  itemTitle,
  languageId,
  onDelete,
  onEdit,
  onReorder,
  onToggleActive,
  reorderable = true,
}) => {
  const [focusedId, setFocusedId] = useState();
  const [isDragging, setIsDragging] = useState(false);
  const tableRef = useRef();

  const getContainer = () =>
    tableRef.current
      .getDOMNode()
      .querySelector('.BaseTable__table-main .BaseTable__body');

  const getHelperContainer = () =>
    tableRef.current.getDOMNode().querySelector('.BaseTable__table-main');

  const columns = useMemo(
    () => [
      {
        key: 'order',
        title: '',
        width: 44,
        flexShrink: 0,
        cellRenderer: () => (reorderable ? <DragHandle /> : null),
      },
      {
        key: 'name',
        dataKey: 'name',
        title: itemTitle,
        width: 300,
        flexGrow: 2,
        cellRenderer: ({ cellData }) => (
          <Tooltip text={getLocalized(cellData, languageId)}>
            <span className={styles.itemName}>
              {getLocalized(cellData, languageId)}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'source',
        dataKey: 'is_system',
        title: __('planning-templates.columns.source'),
        width: 90,
        flexShrink: 0,
        cellRenderer: ({ rowData }) => (
          <Tooltip
            text={
              rowData.is_system
                ? __('planning-templates.source.system')
                : __('planning-templates.source.my-company')
            }
          >
            <span
              className={classNames(
                styles.source,
                rowData.is_system ? styles.system : styles.company
              )}
            >
              {rowData.is_system ? <LogoIcon /> : 'MY'}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'code',
        dataKey: 'code',
        title: __('planning-templates.columns.code'),
        width: 240,
        flexShrink: 0,
        cellRenderer: ({ cellData }) => (
          <Tooltip text={cellData || ''}>
            <span className={styles.code}>{cellData || '—'}</span>
          </Tooltip>
        ),
      },
      {
        key: 'description',
        dataKey: 'description',
        title: __('planning-templates.columns.description'),
        width: 360,
        flexGrow: 3,
        cellRenderer: ({ cellData }) => (
          <Tooltip text={getLocalized(cellData, languageId) || ''}>
            <span className={styles.description}>
              {getLocalized(cellData, languageId) || '—'}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'active',
        dataKey: 'active',
        title: __('planning-templates.columns.active'),
        width: 110,
        flexShrink: 0,
        align: 'center',
        cellRenderer: ({ rowData }) => (
          <div className={styles.activeToggle}>
            {rowData.active ? (
              <CheckedIcon
                className={classNames(
                  styles.checked,
                  rowData.is_system && !canEditSystem && styles.disabled,
                )}
                onClick={
                  rowData.is_system && !canEditSystem
                    ? undefined
                    : () => onToggleActive(rowData, false)
                }
              />
            ) : (
              <UncheckedIcon
                className={classNames(
                  styles.unchecked,
                  rowData.is_system && !canEditSystem && styles.disabled,
                )}
                onClick={
                  rowData.is_system && !canEditSystem
                    ? undefined
                    : () => onToggleActive(rowData, true)
                }
              />
            )}
          </div>
        ),
      },
      {
        key: 'actions',
        title: '',
        width: 64,
        flexShrink: 0,
        align: 'right',
        cellRenderer: ({ rowData }) =>
          rowData.is_system && !canEditSystem && !canDeleteSystem ? null : (
            <Dropdown
              portal
              top={28}
              right={0}
              name={`planning-library-item-${rowData.id}`}
              wrapperClassName={styles.actionsDropdown}
              header={
                <div className={styles.actionsHeader}>
                  {__('content.tooltip.table.rowactions')}
                </div>
              }
              content={
                <div className={styles.actionsContent}>
                  {(!rowData.is_system || canEditSystem) && (
                    <button
                      type='button'
                      onClick={() => {
                        hideDropdown();
                        onEdit(rowData);
                      }}
                    >
                      <EditIcon />
                      {__('button.edit')}
                    </button>
                  )}
                  {(!rowData.is_system || canDeleteSystem) && (
                    <button
                      type='button'
                      className={styles.deleteAction}
                      onClick={() => {
                        hideDropdown();
                        onDelete(rowData);
                      }}
                    >
                      <DeleteIcon />
                      {__('button.delete')}
                    </button>
                  )}
                </div>
              }
            >
              <MenuHIcon className={styles.menuIcon} />
            </Dropdown>
          ),
      },
    ],
    [
      canDeleteSystem,
      canEditSystem,
      itemTitle,
      languageId,
      onDelete,
      onEdit,
      onToggleActive,
      reorderable,
    ],
  );

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    setIsDragging(false);

    if (oldIndex === newIndex) {
      return;
    }

    const reordered = [...data];
    const [item] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, item);

    onReorder(reordered);
  };

  return (
    <div className={styles.table}>
      <AutoSizer>
        {({ height, width }) => (
          <DraggableContainer
            useDragHandle
            getContainer={getContainer}
            helperContainer={getHelperContainer}
            lockAxis='y'
            onSortStart={() => setIsDragging(true)}
            onSortEnd={handleSortEnd}
          >
            <BaseTable
              ref={tableRef}
              className={classNames(
                styles.baseTable,
                isDragging && styles.isDragging
              )}
              ignoreFunctionInColumnCompare={false}
              columns={columns}
              data={data}
              width={width}
              height={height}
              headerHeight={52}
              headerClassName={styles.tableHeader}
              rowHeight={56}
              rowKey='id'
              rowClassName={({ rowData }) =>
                classNames(
                  styles.tableRow,
                  rowData.id === focusedId && styles.focusedRow
                )
              }
              rowProps={({ rowIndex }) => ({
                tagName: PlanningLibraryRow,
                index: rowIndex,
              })}
              onRowClick={({ rowData }) => setFocusedId(rowData.id)}
              emptyRenderer={
                <div className={styles.emptyState}>{emptyText}</div>
              }
            />
          </DraggableContainer>
        )}
      </AutoSizer>
    </div>
  );
};

export default PlanningLibraryTable;
